/**
 * Document Repository (obj_type "K") API client.
 *
 * Two exports:
 *
 * - the **designer** calls (model load/save, provider status, types, rights,
 *   jobs, bulk import) used by DocumentDesignerPage;
 * - `makeDmsClient(project, repo)`, which adapts those endpoints to the
 *   `DmsClient` interface the SHARED `@twasta/page-runtime/dms` views consume.
 *
 * The adapter exists so the run views are written once. The deployed runtime
 * builds the same object against its own `/app/<name>/api/dms/…` prefix, and
 * the two surfaces cannot drift because they render from the same components.
 */

import api from './client'
import type {
  DmsAckReportRow, DmsAnswer, DmsApprovalDetail, DmsAuditEntry, DmsBaseline,
  DmsBrowseResult, DmsCitation, DmsClient, DmsCompound, DmsDocType,
  DmsDocumentDetail, DmsDocumentSource, DmsFolder, DmsItemChangeResult, DmsLookupResult,
  DmsOutstandingAck, DmsPendingApproval, DmsPendingSignature,
  DmsPreflight, DmsSavedFilterList, DmsSignatureChallenge, DmsSignatureStatus,
  DmsSignatureVerification, DmsSigningCredential,
  DmsUploadPolicy, DmsUser,
  DmsValidationFailure, DmsWhereUsedRow,
} from '@twasta/page-runtime/dms'
import type {
  AuthoringClient, AuthoringComparison, AuthoringSession, DocumentTemplate,
} from '@twasta/page-runtime/dms/authoring'

/**
 * The repository's own transport, so a refusal reaches the screen intact.
 *
 * The `DmsClient` contract says the HOST supplies error normalisation, and this
 * one was not. Axios rejects with "Request failed with status code 400", so the
 * server's actual message — "not ready for review: mandatory metadata 'SOP ID'
 * is empty; …" — was thrown away and the user was told a status code. The DMS
 * writes careful, specific refusals (a folder that refuses a type, a file whose
 * bytes disagree with its name, a transition with no approval chain); every one
 * of them was arriving as the same sentence.
 *
 * The runtime twin has always done this (`asApiError`), which is why the
 * deployed app showed the real reason and the platform did not.
 */
function dmsMessage(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response
  const body = response?.data as { detail?: unknown; message?: string } | undefined
  const detail = body?.detail ?? body?.message
  if (typeof detail === 'string' && detail.trim()) return detail
  if (detail && typeof detail === 'object') {
    const envelope = (detail as {
      summary?: unknown; detail?: unknown; message?: unknown
    })
    if (typeof envelope.summary === 'string') return envelope.summary
    if (typeof envelope.detail === 'string') return envelope.detail
    // A refused metadata rule. `_handle` sends {error, message, failures} —
    // the failures carry the sentence the DESIGNER wrote ("SOP is required"),
    // and reading only `summary` (the transaction envelope's key) threw every
    // one of them away, so a blocked save reached the screen as "Request
    // failed with status code 422" and read as a validation that never ran.
    const blocking = dmsFailures(error).filter((f) => (f.severity || 'E') === 'E')
    const said = (blocking.length ? blocking : dmsFailures(error))
      .map((f) => String(f.message || '')).filter(Boolean)
    if (said.length) return said.join('; ')
    if (typeof envelope.message === 'string' && envelope.message.trim()) {
      return envelope.message
    }
  }
  if (error instanceof Error && error.message) return error.message
  return 'The repository could not complete that.'
}

/** The per-field failures on a 422, or `[]` for any other refusal. The shape
 *  is the shared `DmsValidationFailure`; `metadataFailures` in the page-runtime
 *  contracts is what components read it back with. */
function dmsFailures(error: unknown): DmsValidationFailure[] {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  const detail = (data as { detail?: unknown })?.detail
  const rows = (detail as { failures?: unknown })?.failures
  return Array.isArray(rows) ? rows as DmsValidationFailure[] : []
}

/** Wrap a transport failure so the server's own words survive, and so do the
 *  structured failures a form needs to highlight a field. */
function dmsError(error: unknown): Error {
  const wrapped = new Error(dmsMessage(error))
  // Keep the original reachable: callers that branch on a status code (or
  // want the envelope) must not lose it to a friendlier message.
  ;(wrapped as { cause?: unknown }).cause = error
  const failures = dmsFailures(error)
  if (failures.length) {
    (wrapped as { failures?: DmsValidationFailure[] }).failures = failures
  }
  return wrapped
}

async function dmsPost(url: string, body?: unknown, config?: unknown) {
  try {
    return await api.post(url, body as never, config as never)
  } catch (error) {
    throw dmsError(error)
  }
}

async function dmsGet(url: string, config?: unknown) {
  try {
    return await api.get(url, config as never)
  } catch (error) {
    throw dmsError(error)
  }
}

export interface DmsRepositorySummary {
  obj_name: string
  title: string
  description: string
  backend: string
  document_types: number
  qa_enabled: boolean
}

export interface DmsProviderStatus {
  storage: { backend: string; ok: boolean; detail: string; capabilities: string[] }
  malware: { configured: boolean; backend: string; ok?: boolean; required: boolean; detail: string }
  jobs: Record<string, number>
  /** Whether Q&A is actually answering from VECTORS. A configured embedding
   *  profile, an embedding column, an index over it and an embedded corpus
   *  are four separate things, and each fails quietly on its own. */
  embedding?: {
    backend: 'pgvector' | 'text' | string
    /** The chunk table has an `embedding vector(n)` column. */
    column: boolean
    /** …and a nearest-neighbour index over it (otherwise every question
     *  scans every chunk). */
    indexed: boolean
    chunks: number
    embedded: number
    pending: number
    model: string
    dims: number
  }
}

// ── designer ───────────────────────────────────────────────────────────────

export async function listRepositories(project: string) {
  const res = await dmsGet('/dms/list', { params: { project } })
  return res.data as { success: boolean; repositories: DmsRepositorySummary[] }
}

export async function loadRepository(project: string, repo: string) {
  const res = await dmsGet('/dms/model', { params: { project, repo } })
  return res.data as {
    success: boolean
    content: Record<string, unknown> | null
    default?: Record<string, unknown>
  }
}

export async function saveRepository(
  project: string, repo: string, model: Record<string, unknown>,
) {
  const res = await dmsPost('/dms/model', { project, repo, model })
  return res.data as { success: boolean; path: string }
}

export async function providerStatus(project: string, repo: string) {
  const res = await dmsPost('/dms/provider-status', { project, repo })
  return res.data as { success: boolean } & DmsProviderStatus
}

export async function listTypes(project: string, repo: string) {
  const res = await dmsPost('/dms/types', { project, repo })
  return res.data as { success: boolean; types: DmsDocType[] }
}

export async function saveType(
  project: string, repo: string, type: Record<string, unknown>,
) {
  const res = await dmsPost('/dms/types/save', { project, repo, type })
  return res.data as { success: boolean; id: string }
}

export async function listAcl(project: string, repo: string) {
  const res = await dmsPost('/dms/acl', { project, repo })
  return res.data as { success: boolean; acl: Record<string, unknown>[] }
}

export async function saveAcl(project: string, repo: string, entry: {
  scope: string; scope_id?: string; principal_kind: string
  principal_id: string; rights: Record<string, boolean>
}) {
  const res = await dmsPost('/dms/acl/save', { project, repo, ...entry })
  return res.data as { success: boolean; id: string }
}

// ── signing credentials ────────────────────────────────────────────────────
//
// Registering the certificate an organisation signs with is DESIGNER work —
// the same kind of act as declaring which slots a document type has — so these
// sit with the design plane. Signing itself stays on the runtime client, where
// the signer is.

export async function listSigningCredentials(project: string, repo: string) {
  const res = await dmsPost('/dms/signatures/credentials', { project, repo })
  return res.data as {
    success: boolean
    credentials: DmsSigningCredential[]
    pki_available: { ok: boolean; detail: string }
  }
}

export async function registerSigningCredential(
  project: string, repo: string, input: {
    file: File; label: string; passphrase?: string
    allowedProfiles?: string[]
  },
) {
  const form = new FormData()
  form.append('file', input.file)
  form.append('project', project)
  form.append('repo', repo)
  form.append('label', input.label)
  form.append('passphrase', input.passphrase || '')
  form.append('allowed_profiles', (input.allowedProfiles || []).join(','))
  // Same reason the document upload overrides it: axios 1.x otherwise
  // re-serialises the FormData into JSON and the certificate never leaves the
  // browser, which the endpoint then reports as a missing part.
  const res = await dmsPost('/dms/signatures/credentials/register', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data as { success: boolean; credential_id: string }
    & Partial<DmsSigningCredential>
}

export async function signingCredentialHealth(
  project: string, repo: string, credentialId: string,
) {
  const res = await dmsPost('/dms/signatures/credentials/health',
                            { project, repo, credential_id: credentialId })
  return res.data as { success: boolean; ok: boolean; detail: string }
}

export async function deactivateSigningCredential(
  project: string, repo: string, credentialId: string,
) {
  const res = await dmsPost('/dms/signatures/credentials/deactivate',
                            { project, repo, credential_id: credentialId })
  return res.data as { success: boolean; deactivated: boolean }
}


export async function listJobs(project: string, repo: string, status = '') {
  const res = await dmsPost('/dms/jobs', { project, repo }, { params: { status } })
  return res.data as {
    success: boolean
    counts: Record<string, number>
    jobs: Record<string, unknown>[]
  }
}

export async function retryJob(project: string, repo: string, jobId: string) {
  const res = await dmsPost('/dms/jobs/retry', { project, repo }, { params: { job_id: jobId } })
  return res.data as { success: boolean }
}

export async function drainJobs(project: string, repo: string) {
  const res = await dmsPost('/dms/jobs/drain', { project, repo })
  return res.data as { success: boolean; claimed: number; done: number; parked: number }
}

export interface DmsReindexStatus {
  active: boolean
  pending: number
  running: number
  done: number
  parked: number
  total: number
}

export async function reindexRepository(project: string, repo: string,
                                        documentId = '',
                                        mode: 'full' | 'pending' = 'full') {
  const res = await dmsPost('/dms/jobs/reindex',
                            { project, repo, document_id: documentId, mode })
  return res.data as { success: boolean; queued: number } & DmsReindexStatus
}

export async function reindexStatus(project: string, repo: string) {
  const res = await dmsPost('/dms/jobs/reindex/status', { project, repo })
  return res.data as { success: boolean } & DmsReindexStatus
}

export interface DmsReindexPending {
  documents: number
  indexed: number
  excluded: number
  pending_count: number
  pending_bytes: number
  total_bytes: number
  reasons: { not_indexed: number; profile_changed: number; missing_embeddings: number }
}

export async function reindexPending(project: string, repo: string) {
  const res = await dmsPost('/dms/jobs/reindex/pending', { project, repo })
  return res.data as { success: boolean } & DmsReindexPending
}

export async function bulkImport(project: string, repo: string, input: {
  source: string; path: string; doctype_id?: string; folder_id?: string
  recursive?: boolean; extensions?: string[]
}) {
  const res = await dmsPost('/dms/import', { project, repo, ...input })
  return res.data as { success: boolean; queued: number; files: number; detail: string }
}

export async function ackReport(project: string, repo: string, documentId = '') {
  const res = await dmsPost('/dms/ack/report', { project, repo },
                             { params: { document_id: documentId } })
  return res.data as { success: boolean; assignments: Record<string, unknown>[] }
}

export async function assignAck(project: string, repo: string, input: {
  document_id: string; audience_kind: string; audience_id: string
  due_days?: number; reminder_days?: number
}) {
  const res = await dmsPost('/dms/ack/assign', { project, repo, ...input })
  return res.data as { success: boolean; id: string }
}

export async function auditLog(project: string, repo: string, documentId = '') {
  const res = await dmsPost('/dms/audit', { project, repo },
                             { params: { document_id: documentId } })
  return res.data as { success: boolean; entries: Record<string, unknown>[] }
}

/** Re-exported: the shape now lives in the shared DMS contract, so the queue
 *  component and this transport cannot drift apart. */
export type { DmsPendingApproval }

export async function pendingApprovals(project: string, repo: string) {
  const res = await dmsPost('/dms/approvals/pending', { project, repo })
  return res.data as { success: boolean; pending: DmsPendingApproval[] }
}

export async function approvalDetail(
  project: string, repo: string, instanceId: string,
) {
  const res = await dmsPost('/dms/approvals/get',
                             { project, repo, instance_id: instanceId })
  return res.data as {
    success: boolean
    kind: 'version' | 'review'
    document_id: string
    title: string
    target_state: string
    candidate_version_id: string
    review_id: string
    instance: Record<string, unknown>
    sla: Record<string, unknown>
  }
}

/**
 * Approve or reject a document approval.
 *
 * Deliberately its own endpoint rather than the generic transaction approval
 * inbox: promotion means something different here (move one version pointer,
 * not write a transaction row), and only the repository knows how.
 */
export async function decideApproval(
  project: string, repo: string, instanceId: string,
  decision: 'approve' | 'reject',
  opts: { comment?: string; activityId?: string } = {},
) {
  const res = await dmsPost('/dms/approvals/decide', {
    project, repo,
    instance_id: instanceId,
    decision,
    comment: opts.comment || '',
    activity_id: opts.activityId || '',
  })
  return res.data as {
    success: boolean
    status: 'pending' | 'approved' | 'rejected'
    message: string
    dms?: { reverted: boolean; error?: boolean }
  }
}

// ── the shared-view adapter ────────────────────────────────────────────────

/** Base URL the axios client is configured with, for direct <img>/<a> hrefs. */
function absolute(path: string, params: Record<string, string | number>): string {
  const base = (api.defaults.baseURL || '/api').replace(/\/$/, '')
  const query = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ).toString()
  return `${base}${path}?${query}`
}

export function makeDmsClient(project: string, repo: string): DmsClient {
  const body = { project, repo }
  return {
    // ── compound documents ──
    async children(parentId) {
      const res = await dmsPost('/dms/children', { ...body, parent_id: parentId })
      return res.data as DmsCompound
    },
    async addChild(parentId, childId, opts = {}) {
      const res = await dmsPost('/dms/children/add', {
        ...body, parent_id: parentId, child_id: childId, ...opts,
      })
      return res.data
    },
    async updateChild(edgeId, values) {
      const res = await dmsPost('/dms/children/update',
                                 { ...body, edge_id: edgeId, ...values })
      return res.data
    },
    async removeChild(edgeId) {
      const res = await dmsPost('/dms/children/remove', { ...body, edge_id: edgeId })
      return res.data
    },
    async reorderChildren(parentId, order) {
      const res = await dmsPost('/dms/children/reorder',
                                 { ...body, parent_id: parentId, order })
      return res.data
    },
    async whereUsed(documentId, recursive = false) {
      const res = await dmsPost('/dms/where-used',
                                 { ...body, document_id: documentId, recursive })
      return res.data as { parents: DmsWhereUsedRow[]; hidden_parents: number }
    },
    async impact(documentId) {
      const res = await dmsPost('/dms/impact', { ...body, document_id: documentId })
      return res.data as Record<string, unknown>
    },
    async baselines(documentId) {
      const res = await dmsPost('/dms/baselines', { ...body, document_id: documentId })
      return res.data as { baselines: DmsBaseline[] }
    },
    async freezeBaseline(documentId) {
      const res = await dmsPost('/dms/baselines/freeze',
                                 { ...body, document_id: documentId })
      return res.data
    },

    // ── signatures ──
    async signatureStatus(documentId) {
      const res = await dmsPost('/dms/signatures',
                                 { ...body, document_id: documentId })
      return res.data as DmsSignatureStatus
    },
    async documentSource(documentId) {
      const res = await dmsPost('/dms/document-source',
                                 { ...body, document_id: documentId })
      return (res.data?.source || null) as DmsDocumentSource | null
    },
    async pendingSignatures() {
      const res = await dmsPost('/dms/signatures/pending', body)
      return (res.data?.pending || []) as DmsPendingSignature[]
    },
    async openSignatureRequest(documentId) {
      const res = await dmsPost('/dms/signatures/open',
                                 { ...body, document_id: documentId })
      return res.data as { request_id: string }
    },
    async sign(requestId, opts = {}) {
      const res = await dmsPost('/dms/signatures/sign', {
        ...body, request_id: requestId,
        slot_id: opts.slot_id || '',
        reason: opts.reason || '',
        // The CREDENTIAL, never a count (Phase 0.1). The server verifies it
        // and derives how many identification components were proved; an
        // `auth_components` posted by a client is not read at all.
        password: opts.password || '',
        otp_code: opts.otp_code || '',
        // The drawn mark, when the slot asked for one. It travels with the
        // credential and is NOT part of it: what proves who signed is the
        // credential above, and the server drops ink offered for a slot that
        // did not ask so the design stays the authority on which slots carry
        // a mark.
        ink: opts.ink || '',
        // WHAT was read (D10). The server recomputes it and refuses a step
        // that has moved since, so a signature is about something rather than
        // about whatever the record said when the click arrived.
        content_token: opts.content_token || '',
      })
      return res.data
    },
    async repairExecutedCopies() {
      const res = await dmsPost('/dms/signatures/executed-copies/repair', body)
      return res.data
    },
    async verifySignatures(documentId, versionId) {
      const res = await dmsPost('/dms/signatures/verify', {
        ...body, document_id: documentId, version_id: versionId || '',
      })
      return res.data as DmsSignatureVerification
    },
    async signatureChallenge(requestId, slotId) {
      const res = await dmsPost('/dms/signatures/challenge', {
        ...body, request_id: requestId, slot_id: slotId || '',
      })
      return res.data as DmsSignatureChallenge
    },
    async declineSignature(requestId, slotId, reason = '') {
      const res = await dmsPost('/dms/signatures/decline',
                                 { ...body, request_id: requestId,
                                   slot_id: slotId, reason })
      return res.data
    },
    signatureManifestUrl(documentId) {
      return absolute('/dms/signatures/manifest',
                      { project, repo, document_id: documentId })
    },

    async browse(input) {
      const res = await dmsPost('/dms/browse', {
        ...body,
        query: input.query || '',
        doctype_id: input.doctypeId || '',
        folder_id: input.folderId || '',
        states: input.states || null,
        owner: input.owner || '',
        order: input.order || 'recent',
        doctype_ids: input.doctypeIds || null,
        owners: input.owners || null,
        conditions: input.conditions || null,
        limit: input.limit ?? 50,
        offset: input.offset ?? 0,
      })
      return res.data as DmsBrowseResult
    },

    async savedFilters() {
      const res = await dmsPost('/dms/filters', body)
      return res.data as DmsSavedFilterList
    },

    async saveFilter(input) {
      const res = await dmsPost('/dms/filters/save', {
        ...body,
        name: input.name,
        filter: input.filter,
        shared: !!input.shared,
        filter_id: input.filterId || '',
      })
      return res.data as { filter_id: string }
    },

    async deleteFilter(filterId) {
      const res = await dmsPost('/dms/filters/delete', {
        ...body, filter_id: filterId,
      })
      return res.data
    },

    async detail(documentId) {
      const res = await dmsPost('/dms/document', { ...body, document_id: documentId })
      return res.data as DmsDocumentDetail
    },

    async types() {
      const res = await dmsPost('/dms/types', body)
      return (res.data?.types || []) as DmsDocType[]
    },

    async folders() {
      const res = await dmsPost('/dms/folders', body)
      return (res.data?.folders || []) as DmsFolder[]
    },

    async upload(input) {
      const form = new FormData()
      form.append('file', input.file)
      form.append('project', project)
      form.append('repo', repo)
      form.append('title', input.title || '')
      form.append('doctype_id', input.doctypeId || '')
      form.append('folder_id', input.folderId || '')
      form.append('document_id', input.documentId || '')
      form.append('major', String(Boolean(input.major)))
      // The metadata rides WITH the file.
      //
      // This part was missing, and nothing failed: the panel collected the SOP
      // details, handed them to `upload`, and this client dropped them on the
      // floor. The server saw no metadata, filled the type's declared defaults
      // (Owner and Reviewer resolve from `{{user}}`) and filed the document —
      // so an upload reported success and came back with every typed field
      // empty. The endpoint has always accepted the part; the runtime client
      // has always sent it. Only this half of the pair did not.
      form.append('metadata',
        input.metadata ? JSON.stringify(input.metadata) : '')
      // The shared axios client defaults to application/json; without this
      // override axios 1.x re-serialises the FormData INTO json and the file
      // never leaves the browser (the endpoint then 422s on the missing part).
      const res = await dmsPost('/dms/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data as {
        document_id: string; version_id: string
        advisories?: DmsValidationFailure[]
      }
    },

    async saveMetadata(documentId, values, opts) {
      const res = await dmsPost('/dms/metadata/save', {
        ...body, document_id: documentId, values,
        ignore_warnings: opts?.ignoreWarnings || [],
      })
      // The advisories a 'W'/'P' rule raised. They do not block, so this
      // response is the ONLY place they exist — dropping it made a
      // warning-severity check look like a check that never ran.
      const advisories = (res.data as { advisories?: unknown })?.advisories
      return Array.isArray(advisories) ? advisories : []
    },

    // A metadata field's reference picker. The filter is NOT sent — the server
    // reads it off the stored field definition, so a reader cannot post a
    // WHERE fragment.
    async metadataLookup(input) {
      const res = await dmsPost('/dms/metadata/lookup', {
        ...body, doctype_id: input.doctypeId, field_key: input.fieldKey,
        search: input.search || '', limit: input.limit || 50,
        values: input.values || {},
      })
      return res.data as DmsLookupResult
    },

    // The on-change rule attached to a field. A field with no rule answers
    // with empty updates rather than an error, so the caller may ask on every
    // change without first knowing which fields carry one.
    async metadataItemChange(input) {
      const res = await dmsPost('/dms/metadata/item-change', {
        ...body, doctype_id: input.doctypeId, field_key: input.fieldKey,
        values: input.values || {},
      })
      return res.data as DmsItemChangeResult
    },

    async confirmMetadata(documentId, fields) {
      await dmsPost('/dms/metadata/confirm', { ...body, document_id: documentId, fields })
    },

    async act(documentId, action, comment) {
      const res = await dmsPost('/dms/act', {
        ...body, document_id: documentId, action, comment: comment || '',
      })
      return res.data as { staged?: boolean; target_state?: string }
    },

    async checkOut(documentId) {
      await dmsPost('/dms/checkout', { ...body, document_id: documentId })
    },

    async cancelCheckOut(documentId) {
      await dmsPost('/dms/checkout/cancel', { ...body, document_id: documentId })
    },

    async authedBlobUrl(url) {
      // Through the axios client, so the interceptor attaches whatever
      // identifies this caller — the platform cookie, or a project user's
      // run-as headers and bearer token, which an <img> or <a> could never
      // carry by itself.
      const path = url.startsWith(api.defaults.baseURL || '/api')
        ? url.slice((api.defaults.baseURL || '/api').length)
        : url
      const res = await api.get(path, { responseType: 'blob' })
      return URL.createObjectURL(res.data as Blob)
    },
    pageUrl(documentId, page, versionId) {
      return absolute('/dms/page', {
        project, repo, document_id: documentId, page,
        version_id: versionId || '',
      })
    },

    thumbnailUrl(documentId, versionId) {
      return absolute('/dms/thumbnail', {
        project, repo, document_id: documentId, version_id: versionId || '',
      })
    },

    metadataImageUrl(documentId, fieldKey) {
      return absolute('/dms/metadata/image', {
        project, repo, document_id: documentId, field_key: fieldKey,
      })
    },

    downloadUrl(documentId, versionId) {
      return absolute('/dms/download', {
        project, repo, document_id: documentId, version_id: versionId || '',
      })
    },

    async ask(question, opts) {
      const res = await dmsPost('/dms/ask', {
        ...body, question,
        historical: Boolean(opts?.historical),
        document_ids: opts?.documentIds || null,
      })
      return res.data as DmsAnswer
    },

    async search(question) {
      const res = await dmsPost('/dms/search', { ...body, question })
      return res.data as { hits: DmsCitation[]; backend: string }
    },

    async compare(documentId, a, b) {
      const res = await dmsPost('/dms/compare', {
        ...body, document_id: documentId, version_a: a, version_b: b,
      })
      return res.data as { summary: string; diff: string[] }
    },

    // ── approvals ──
    // The runtime twin deliberately omits these: a downloaded copy of a
    // controlled repository has no approval engine, so its client leaves the
    // methods undefined and the queue hides itself rather than 404-ing.
    async pendingApprovals() {
      const res = await pendingApprovals(project, repo)
      return (res.pending || []) as DmsPendingApproval[]
    },
    async approvalDetail(instanceId) {
      const res = await approvalDetail(project, repo, instanceId)
      return {
        kind: res.kind,
        document_id: res.document_id,
        title: res.title,
        target_state: res.target_state,
        candidate_version_id: res.candidate_version_id,
        review_id: res.review_id,
        instance: res.instance || {},
        sla: (res.sla || {}) as unknown as DmsApprovalDetail['sla'],
      }
    },
    async decideApproval(input) {
      const res = await decideApproval(project, repo, input.instanceId,
                                       input.decision,
                                       { comment: input.comment,
                                         activityId: input.activityId })
      return { status: res.status, message: res.message, dms: res.dms }
    },

    // ── compliance evidence (administrator-only on the server) ──
    async ackReport(documentId = '') {
      const res = await ackReport(project, repo, documentId)
      return (res.assignments || []) as unknown as DmsAckReportRow[]
    },
    async auditLog(input = {}) {
      const res = await auditLog(project, repo, input.documentId || '')
      return (res.entries || []) as unknown as DmsAuditEntry[]
    },

    // ── search re-indexing (gated by the `reindex` right on the server) ──
    async reindex(documentId = '', mode: 'full' | 'pending' = 'full') {
      return await reindexRepository(project, repo, documentId, mode)
    },
    async reindexStatus() {
      return await reindexStatus(project, repo)
    },
    async reindexPending() {
      return await reindexPending(project, repo)
    },

    async outstandingAcks() {
      const res = await dmsPost('/dms/ack/outstanding', body)
      return (res.data?.outstanding || []) as DmsOutstandingAck[]
    },

    async acknowledge(assignmentId) {
      await dmsPost('/dms/ack/confirm', { ...body, assignment_id: assignmentId })
    },

    async feedback(input) {
      await dmsPost('/dms/feedback', {
        ...body,
        rating: input.rating,
        category: input.category || '',
        document_id: input.documentId || '',
        question: input.question || '',
        answer: input.answer || '',
        comment: input.comment || '',
      })
    },

    /**
     * Define a document type, in the repository's own DEFINITION.
     *
     * Two tiers, tried in order, because they mean different things:
     *
     * 1. **The model file.** A document type is part of what the repository
     *    IS — its retention, its review cycle, whether its documents are
     *    written or uploaded. A type that exists only as a database row is
     *    invisible in the model, absent from anything generated from it, and
     *    reads as a local deviation rather than as the design. So the first
     *    attempt appends it to `repository.document_types` and saves. The next
     *    request re-seeds (`context.build` seeds every time), and the designer's
     *    Types tab shows it beside the rest.
     * 2. **A runtime row**, when the caller may administer the repository but
     *    not edit the project's objects. That is the pre-existing capability
     *    and it is stamped `runtime`, so a later design seed leaves it alone.
     *
     * Falling back rather than failing matters: a runtime administrator adding
     * a type mid-upload should not be told to go and get designer rights.
     */
    async createType(input) {
      const spec = {
        key: input.key,
        title: input.title,
        authoring: { mode: input.authoring || 'off' },
        ...(input.retentionYears ? { retention: { years: input.retentionYears } } : {}),
        ...(input.reviewMonths ? { review: { months: input.reviewMonths } } : {}),
        fields: input.fields || [],
      }
      try {
        const current = await dmsGet('/dms/model', { params: { project, repo } })
        const model = current.data?.content || current.data?.default
        if (!model) throw new Error('no model')
        const repository = model.repository || (model.repository = {})
        const list = Array.isArray(repository.document_types)
          ? repository.document_types : []
        const at = list.findIndex((t: { key?: string }) =>
          String(t?.key || '').toUpperCase() === spec.key.toUpperCase())
        // Upsert by key: re-running with an existing key EDITS that type
        // rather than filing a duplicate the seed would then fight over.
        if (at >= 0) list[at] = { ...list[at], ...spec }
        else list.push(spec)
        repository.document_types = list
        await dmsPost('/dms/model', { project, repo, model })
        // The id only exists after the seed, which the next request runs.
        const types = await dmsPost('/dms/types', body)
        const created = (types.data?.types || []).find(
          (t: { type_key?: string }) =>
            String(t?.type_key || '').toUpperCase() === spec.key.toUpperCase())
        return { id: String(created?.id || '') }
      } catch (modelError) {
        // Not a designer on this project — fall back to a runtime type.
        const res = await dmsPost('/dms/types/save', { ...body, type: spec })
        return { id: String(res.data?.id || '') }
      }
    },

    async listUsers() {
      const res = await dmsPost('/dms/users', body)
      return (res.data?.users || []) as DmsUser[]
    },

    async createFolder(input) {
      const res = await dmsPost('/dms/folders/create', {
        ...body, name: input.name, parent_id: input.parentId || '',
      })
      return { id: String(res.data?.id || '') }
    },

    async moveFolder(input) {
      const res = await dmsPost('/dms/folders/move', {
        ...body,
        folder_id: input.folderId,
        parent_id: input.parentId || '',
        position: input.position ?? null,
      })
      return {
        folder_id: String(res.data?.folder_id || input.folderId),
        parent_id: String(res.data?.parent_id || ''),
        position: Number(res.data?.position ?? 0),
      }
    },

    async folderCounts() {
      const res = await dmsPost('/dms/folders/counts', body)
      const raw = (res.data?.counts || {}) as Record<string, unknown>
      const out: Record<string, number> = {}
      for (const [key, value] of Object.entries(raw)) out[key] = Number(value) || 0
      return out
    },

    async rename(documentId, title) {
      const res = await dmsPost('/dms/document/rename', {
        ...body, document_id: documentId, title,
      })
      return { title: String(res.data?.title || title) }
    },

    async move(documentId, folderId) {
      const res = await dmsPost('/dms/document/move', {
        ...body, document_id: documentId, folder_id: folderId || '',
      })
      return { folder_id: String(res.data?.folder_id ?? folderId ?? '') }
    },

    // ── review feedback ──────────────────────────────────────────────────
    async submitReview(input) {
      const res = await dmsPost('/dms/reviews/submit', {
        ...body,
        document_id: input.documentId,
        version_id: input.versionId || '',
        summary: input.summary || '',
        items: input.items,
      })
      return { review_id: String(res.data?.review_id || ''),
               items: Number(res.data?.items || 0) }
    },

    async listReviews(input) {
      const res = await dmsPost('/dms/reviews', {
        ...body,
        document_id: input?.documentId || '',
        statuses: input?.statuses || null,
      })
      return res.data?.reviews || []
    },

    async decideReview(input) {
      const res = await dmsPost('/dms/reviews/decide', {
        ...body, review_id: input.reviewId,
        accepted: input.accepted || [], rejected: input.rejected || [],
        note: input.note || '',
      })
      return { accepted: Number(res.data?.accepted || 0),
               rejected: Number(res.data?.rejected || 0),
               open: Number(res.data?.open || 0) }
    },

    async approveReview(input) {
      const res = await dmsPost('/dms/reviews/approve', {
        ...body, review_id: input.reviewId, comment: input.comment || '',
      })
      return { instance_id: String(res.data?.instance_id || '') }
    },

    async draftRevision(input) {
      const res = await dmsPost('/dms/reviews/draft', {
        ...body, document_id: input.documentId,
        version_id: input.versionId || '',
        use_ai: input.useAi !== false,
      })
      return { version_id: String(res.data?.version_id || ''),
               items: Number(res.data?.items || 0),
               method: String(res.data?.method || '') }
    },
  }
}

/**
 * The AUTHORING transport, adapted the same way `makeDmsClient` adapts the
 * repository one — and kept separate from it on purpose.
 *
 * A repository that authors nothing never loads the editor, and a host that
 * cannot author (a surface with no `/authoring/*` routes) simply does not build
 * this object. Folding these five methods into `DmsClient` would have made
 * every consumer of the repository views depend on an editor most of them never
 * open.
 */
export function makeAuthoringClient(project: string, repo: string): AuthoringClient {
  const body = { project, repo }
  return {
    async templates() {
      const res = await dmsPost('/dms/authoring/templates', body)
      return res.data as {
        templates: DocumentTemplate[]
        elements: Array<{ id: string; label: string; structural: boolean }>
        document_keys: string[]
        list_numbering: string[]
      }
    },

    async newDocument(doctypeId) {
      const res = await dmsPost('/dms/authoring/new', {
        ...body, doctype_id: doctypeId,
      })
      return res.data as AuthoringSession
    },

    async open(documentId, versionId) {
      const res = await dmsPost('/dms/authoring/open', {
        ...body, document_id: documentId, version_id: versionId || '',
      })
      return res.data as AuthoringSession
    },

    async save(input) {
      // Multipart, not JSON: the PDF IS the payload, and base64 inside a JSON
      // body would inflate it by a third and hold both copies in memory.
      const form = new FormData()
      form.append('pdf', input.pdf, 'document.pdf')
      form.append('project', project)
      form.append('repo', repo)
      form.append('content', JSON.stringify(input.content))
      form.append('title', input.title || '')
      form.append('document_id', input.documentId || '')
      form.append('doctype_id', input.doctypeId || '')
      form.append('folder_id', input.folderId || '')
      form.append('template', input.template ? JSON.stringify(input.template) : '')
      form.append('template_key', input.templateKey || '')
      form.append('page_layout',
        input.pageLayout ? JSON.stringify(input.pageLayout) : '')
      form.append('major', String(Boolean(input.major)))
      form.append('metadata',
        input.metadata ? JSON.stringify(input.metadata) : '')
      const res = await dmsPost('/dms/authoring/save', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data as {
        document_id: string
        version_id: string
        page_count: number
        size_bytes: number
        title: string
      }
    },

    async compare(documentId, a, b) {
      const res = await dmsPost('/dms/authoring/compare', {
        ...body, document_id: documentId, version_a: a, version_b: b,
      })
      return res.data as AuthoringComparison
    },
    async importFile(file: File) {
      const form = new FormData()
      form.append('file', file)
      form.append('project', project)
      form.append('repo', repo)
      const res = await dmsPost('/dms/authoring/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return {
        html: String(res.data?.html || ''),
        format: String(res.data?.format || ''),
        warnings: (res.data?.warnings || []) as string[],
      }
    },

    async choices() {
      // The repository's own endpoints, not authoring-specific ones: the
      // question "which types and folders exist" has one answer and it must
      // not come from two places that can disagree.
      const [types, folders] = await Promise.all([
        dmsPost('/dms/types', body),
        dmsPost('/dms/folders', body),
      ])
      return {
        doctypes: ((types.data?.types || []) as Array<Record<string, unknown>>)
          .map((t) => ({
            id: String(t.id || ''),
            title: String(t.title || t.type_key || ''),
            authoring_mode: String(
              (t.authoring as { mode?: string } | undefined)?.mode || 'off'),
          })),
        folders: ((folders.data?.folders || []) as Array<Record<string, unknown>>)
          .map((f) => ({
            id: String(f.id || ''),
            name: String(f.name || ''),
            parent_id: String(f.parent_id || ''),
          })),
      }
    },
  }
}
